import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type Theme } from '@mui/material/styles';
import { Controller, type Control, useWatch } from 'react-hook-form';

import { BODYPRINT_PRIVACY_URL } from '@/config/links';
import { type OnboardingMode, type OnboardingStepKey } from '@/domain/onboarding/types';
import { type OnboardingFormValues } from '@/domain/onboarding/types';
import { partnerCategoryLabel } from '@/domain/onboarding/utils';
import { useTranslate } from '@/state/i18n';
import { surfaceTokens } from '@/theme/themePrimitives';
import { getTaxIdLabel } from '@/utils/data';

interface Props {
  control: Control<OnboardingFormValues>;
  mode: OnboardingMode;
  onEditStep: (step: OnboardingStepKey) => void;
}

/** Step 3 (design P04 · 3): everything at a glance, the terms (first submission only) and send. */
const ReviewStep = ({ control, mode, onEditStep }: Props) => {
  const t = useTranslate('domain.onboarding.components.ReviewStep');
  const values = useWatch({ control });

  const rows: { key: string; label: string; value: string; step: OnboardingStepKey }[] = [
    {
      key: 'organization',
      label: t('organization'),
      value: [values.displayName, values.category ? partnerCategoryLabel[values.category] : '']
        .filter(Boolean)
        .join(' · '),
      step: 'organization',
    },
    { key: 'legal', label: t('legalName'), value: values.legalName ?? '', step: 'organization' },
    {
      key: 'taxId',
      label: getTaxIdLabel(values.taxId),
      value: values.taxId ?? '',
      step: 'organization',
    },
    {
      key: 'contact',
      label: t('contact'),
      value: [values.contactName, values.contactEmail, values.contactPhone]
        .filter(Boolean)
        .join(' · '),
      step: 'contact',
    },
    {
      key: 'region',
      label: t('region'),
      value: [values.city, values.state].filter(Boolean).join(' · ') || t('notInformed'),
      step: 'contact',
    },
    {
      key: 'web',
      label: t('web'),
      value: [values.website, values.instagram].filter(Boolean).join(' · ') || t('notInformed'),
      step: 'contact',
    },
  ];

  return (
    <Stack gap={2.5}>
      <Box component="dl" sx={sx.list}>
        {rows.map((row) => (
          <Box key={row.key} sx={sx.row}>
            <Typography component="dt" variant="body2" color="text.secondary">
              {row.label}
            </Typography>
            <Typography component="dd" variant="body2" sx={sx.value}>
              {row.value}
            </Typography>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => onEditStep(row.step)}
            >
              {t('edit')}
            </Link>
          </Box>
        ))}
      </Box>
      {mode === 'create' ? (
        <Controller
          name="acceptTerms"
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                    onBlur={field.onBlur}
                    color={fieldState.error ? 'error' : 'primary'}
                  />
                }
                label={
                  <>
                    {t('acceptTermsLead')}
                    <Link href={BODYPRINT_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                      {t('privacyPolicy')}
                    </Link>
                  </>
                }
              />
              {fieldState.error ? (
                <FormHelperText error sx={{ pl: 4 }}>
                  {fieldState.error.message}
                </FormHelperText>
              ) : null}
            </Box>
          )}
        />
      ) : null}
      <Typography variant="body2" color="text.secondary">
        {t(mode === 'create' ? 'reviewNote' : 'resubmitNote')}
      </Typography>
    </Stack>
  );
};

export default ReviewStep;

const sx = {
  list: { m: 0 },
  row: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: '180px minmax(0, 1fr) auto' },
    gap: { xs: 0.5, sm: 2 },
    alignItems: 'baseline',
    py: 1.5,
    borderBottom: `1px solid ${surfaceTokens.borderSubtle}`,
    '&:last-of-type': { borderBottom: 0 },
    ...theme.applyStyles('dark', { borderColor: 'divider' }),
  }),
  value: {
    m: 0,
    gridColumn: { xs: '1 / -1', sm: 'auto' },
    gridRow: { xs: 2, sm: 'auto' },
    wordBreak: 'break-word' as const,
  },
};
